import { Injectable } from '@angular/core';
import {ServiceFacility} from '../../interfaces/core/service-facility';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpService} from './http.service';
import {StorageService} from './storage.service';
import {ServiceFacilityFactory} from '../../factories/core/service-facility-factory';
import {map, tap} from 'rxjs/operators';
import {UserService} from './user.service';
import {db} from '../../tables/db.service';

@Injectable({
  providedIn: 'root'
})
export class ServiceFacilitiesService {

  private static readonly _storageKey = 'selected-service-facility';
  private static readonly _relevantServiceFacilityIdsKey = 'relevant-service-facility-ids';

  private readonly _selectedServiceFacility$: BehaviorSubject<ServiceFacility>;
  private _selectedServiceFacility: ServiceFacility = null;
  private _relevantServiceFacilityIds: number[] = [];

  /**
   *
   * @param httpService
   * @param storageService
   */
  constructor(private httpService: HttpService,
              private storageService: StorageService) {
    this._selectedServiceFacility$ = new BehaviorSubject<ServiceFacility>(this._selectedServiceFacility);
  }

  get selectedServiceFacility$(): BehaviorSubject<ServiceFacility> {
    return this._selectedServiceFacility$;
  }

  get selectedServiceFacility(): ServiceFacility {
    return this._selectedServiceFacility;
  }

  public async setRelevantServiceFacilityIds(relevantServiceFacilityIds: number[]): Promise<void> {
    this._relevantServiceFacilityIds = relevantServiceFacilityIds;
    await this.storageService.setItem(ServiceFacilitiesService._relevantServiceFacilityIdsKey, relevantServiceFacilityIds);
  }

  /**
   *
   */
  public async init(): Promise<void> {
    const selectedServiceFacility =
      await this.storageService.getItem(ServiceFacilitiesService._storageKey, null );

    this._relevantServiceFacilityIds =
      await this.storageService.getItem(ServiceFacilitiesService._relevantServiceFacilityIdsKey, [] );

    if(selectedServiceFacility) {
      await this.setSelectedServiceFacility(selectedServiceFacility);
    }
  }

  /**
   *
   */
  public loadServiceFacilities(): Observable<ServiceFacility[]> {
    return this.httpService.get<any>({
      url: ['service-facilities']
    }).pipe(map((_serviceFacilities) => {
      return ServiceFacilityFactory.fromArray(_serviceFacilities);
    }), tap((_serviceFacilities) => {
      this.setServiceFacilities(_serviceFacilities).finally();
    }));
  }

  /**
   *
   * @param serviceFacility
   */
  public async setSelectedServiceFacility(serviceFacility: ServiceFacility): Promise<void> {
    this._selectedServiceFacility = serviceFacility;
    await this.storageService.setItem(ServiceFacilitiesService._storageKey, serviceFacility);
    this._selectedServiceFacility$.next(this._selectedServiceFacility);
  }

  /**
   *
   */
  public async setSelectedServiceFacilityById(serviceFacilityId: number): Promise<void> {
    const serviceFacility = await db.serviceFacilities.get(serviceFacilityId);
    if(serviceFacility) {
      await this.setSelectedServiceFacility(serviceFacility);
    }
  }

  /**
   *
   */
  public async getServiceFacilitiesFromUser(): Promise<ServiceFacility[]> {
    const serviceFacilities = await db.serviceFacilities.toArray();
    return serviceFacilities.filter(_serviceFacility => {
      const serviceFacilityIds = [
        ...UserService.getUser().serviceFacilityIds,
        ...this._relevantServiceFacilityIds
      ];
      return serviceFacilityIds.some(_serviceFacilityId => _serviceFacility.id === _serviceFacilityId);
    });
  }

  public async getServiceFacilities(serviceFacilityIds: number[]): Promise<ServiceFacility[]> {
    const serviceFacilities = await db.serviceFacilities.toArray();
    return serviceFacilities.filter(_serviceFacility => {
      return serviceFacilityIds.some(_serviceFacilityId => _serviceFacility.id === _serviceFacilityId);
    });
  }

  /**
   *
   */
  public async clear(): Promise<void> {
    this._selectedServiceFacility = null;
    this._selectedServiceFacility$.next(this._selectedServiceFacility);
    await this.storageService.removeItem(ServiceFacilitiesService._storageKey);
    await db.serviceFacilities.clear();
  }

  /**
   *
   * @param serviceFacilities
   * @private
   */
  private async setServiceFacilities(serviceFacilities: ServiceFacility[]): Promise<void> {
    let selectedServiceFacility: ServiceFacility = null;
    if(this._selectedServiceFacility) {
      selectedServiceFacility = serviceFacilities.find((_serviceFacility) => {
        return _serviceFacility.id === this._selectedServiceFacility.id;
      });
    }

    if(!selectedServiceFacility) {
      selectedServiceFacility = serviceFacilities.find((_serviceFacility) => {
        return _serviceFacility.id === UserService.getUser().employee.serviceFacilityId;
      });
      await this.setSelectedServiceFacility(selectedServiceFacility);
    }
    await db.serviceFacilities.clear();
    await db.serviceFacilities.bulkAdd(serviceFacilities);
  }
}
